// DoubleSlitLabWithSideObserver.jsx
import React, { useRef, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";

// ---------------- Particle Component ----------------
function Particle({ particle }) {
  const ref = useRef();

  useFrame(() => {
    if (!particle.hit) {
      particle.pos.add(particle.vel);
      if (particle.pos.z >= 6) particle.hit = true; // stop at screen behind
      if (ref.current) ref.current.position.copy(particle.pos);
    }
  });

  return (
    <mesh ref={ref} position={particle.pos.toArray()}>
      <sphereGeometry args={[0.05, 12, 12]} />
      <meshStandardMaterial color="#0ff" emissive="#0ff" />
    </mesh>
  );
}

// ---------------- Wall with Two Vertical Slits ----------------
function Wall({ slits, z = 0, width = 6, height = 3 }) {
  const wallThickness = 0.1;
  const meshes = [];
  const leftEdge = -width / 2;
  const rightEdge = width / 2;

  // Left of first slit
  meshes.push(
    <mesh key="left" position={[(slits[0].left + leftEdge) / 2, 0, z]}>
      <boxGeometry args={[slits[0].left - leftEdge, height, wallThickness]} />
      <meshStandardMaterial color="#888" />
    </mesh>
  );

  // Between slits
  meshes.push(
    <mesh key="middle" position={[(slits[0].right + slits[1].left) / 2, 0, z]}>
      <boxGeometry args={[slits[1].left - slits[0].right, height, wallThickness]} />
      <meshStandardMaterial color="#888" />
    </mesh>
  );

  // Right of second slit
  meshes.push(
    <mesh key="right" position={[(slits[1].right + rightEdge) / 2, 0, z]}>
      <boxGeometry args={[rightEdge - slits[1].right, height, wallThickness]} />
      <meshStandardMaterial color="#888" />
    </mesh>
  );

  return <>{meshes}</>;
}

// ---------------- Human Observer (Side View) ----------------
function AnimatedHumanObserver({ position = [3, 0, -3], particles }) {
  const leftArmRef = useRef();
  const rightArmRef = useRef();

  useFrame(() => {
    if (!particles || particles.length === 0) {
      leftArmRef.current.rotation.z = -0.2;
      rightArmRef.current.rotation.z = 0.2;
      return;
    }

    const p = particles[0]; // track first particle
    const dx = p.pos.x - position[0];
    const maxRot = Math.PI / 4;
    const rotZ = Math.max(-maxRot, Math.min(maxRot, dx));
    leftArmRef.current.rotation.z = -0.2 + rotZ * 0.2;
    rightArmRef.current.rotation.z = 0.2 - rotZ * 0.2;
  });

  return (
    <group position={position}>
      {/* Head */}
      <mesh position={[0, 1.5, 0]}>
        <sphereGeometry args={[0.3, 16, 16]} />
        <meshStandardMaterial color="#ff00ff" emissive="#ff00ff" />
      </mesh>
      {/* Body */}
      <mesh position={[0, 0.75, 0]}>
        <cylinderGeometry args={[0.25, 0.25, 1.5, 16]} />
        <meshStandardMaterial color="#ff00ff" emissive="#ff00ff" />
      </mesh>
      {/* Left Arm */}
      <mesh ref={leftArmRef} position={[-0.5, 0.75, 0]} rotation={[0, 0, -0.2]}>
        <cylinderGeometry args={[0.1, 0.1, 1, 8]} />
        <meshStandardMaterial color="#ff00ff" emissive="#ff00ff" />
      </mesh>
      {/* Right Arm */}
      <mesh ref={rightArmRef} position={[0.5, 0.75, 0]} rotation={[0, 0, 0.2]}>
        <cylinderGeometry args={[0.1, 0.1, 1, 8]} />
        <meshStandardMaterial color="#ff00ff" emissive="#ff00ff" />
      </mesh>
    </group>
  );
}

// ---------------- Main Lab ----------------
export default function WaveParticleDuality() {
  const [particles, setParticles] = useState([]);
  const particlesRef = useRef([]);
  const [firedCount, setFiredCount] = useState(0);
  const [observerActive, setObserverActive] = useState(true);

  // Two vertical slits
  const slitWidth = 0.3;
  const slitSeparation = 1.0;
  const wallHeight = 3;
  const slits = [
    { left: -slitSeparation / 2 - slitWidth / 2, right: -slitSeparation / 2 + slitWidth / 2, bottom: -1, top: 1 },
    { left: slitSeparation / 2 - slitWidth / 2, right: slitSeparation / 2 + slitWidth / 2, bottom: -1, top: 1 },
  ];

  const sourcePos = new THREE.Vector3(0, 0, -6);

  // Fire particle
  const fireParticle = () => {
    const chosenSlit = slits[Math.floor(Math.random() * slits.length)];
    const targetX = chosenSlit.left + Math.random() * (chosenSlit.right - chosenSlit.left);
    const targetY = chosenSlit.bottom + Math.random() * (chosenSlit.top - chosenSlit.bottom);
    const targetZ = 0;

    const dir = new THREE.Vector3(targetX - sourcePos.x, targetY - sourcePos.y, targetZ - sourcePos.z).normalize();
    const vel = dir.multiplyScalar(0.05);

    const particle = {
      id: particlesRef.current.length,
      pos: sourcePos.clone(),
      vel,
      hit: false,
    };

    particlesRef.current.push(particle);
    setParticles([...particlesRef.current]);
    setFiredCount((f) => f + 1);
  };

  return (
    <div style={{ height: "100vh", background: "#000", color: "#0ff", padding: 20 }}>
      <h2>Double Slit Lab — Random Slit Firing</h2>

      <div style={{ marginBottom: 12 }}>
        <button
          onClick={() => {
            particlesRef.current = [];
            setParticles([]);
            setFiredCount(0);
          }}
        >
          Reset
        </button>
        <button onClick={fireParticle}>Fire Particle</button>
        <span style={{ marginLeft: 12 }}>Fired: {firedCount}</span>
        <label style={{ marginLeft: 20 }}>
          <input
            type="checkbox"
            checked={observerActive}
            onChange={(e) => setObserverActive(e.target.checked)}
          />{" "}
          Observer Active
        </label>
      </div>

      <Canvas camera={{ position: [0, 0, -8], fov: 90 }}>
        <ambientLight intensity={0.7} />
        <pointLight position={[5, 5, 5]} intensity={1.5} />
        <pointLight position={[-5, -5, -5]} intensity={1.0} />
        <OrbitControls
   
  />

        {/* Human Observer on the side */}
        {observerActive && <AnimatedHumanObserver position={[3, 0, -3]} particles={particles} />}

        {/* Wall with slits */}
        <Wall slits={slits} z={0} width={6} height={wallHeight} />

        {/* Screen */}
        <mesh position={[0, 0, 6]}>
          <planeGeometry args={[6, 3]} />
          <meshStandardMaterial color="#111" emissive="#0ff" emissiveIntensity={0.1} side={THREE.DoubleSide} />
        </mesh>

        {/* Yellow Source */}
        <mesh position={sourcePos}>
          <sphereGeometry args={[0.2, 16, 16]} />
          <meshStandardMaterial color="#ffff00" emissive="#ffff00" emissiveIntensity={2.5} />
        </mesh>

        {/* Particles */}
        {particles.map((p) => (
          <Particle key={p.id} particle={p} />
        ))}
      </Canvas>
    </div>
  );
}
